import { Button, Col, Form, Input, Modal, Row, Typography } from "antd";
import {
  COST_TYPE_ADD_ENDPOINT,
  COST_TYPE_DELETE_ENDPOINT,
  COST_TYPE_EDIT_ENDPOINT,
  COST_TYPE_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData, putData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { NO_PERMISSION_REQUIRED } from "routes/permissions";
import { hasPermission } from "utils/functions";

export default function CostType() {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const [costTypeForm] = Form.useForm();
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  const getAllCostTypes = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${COST_TYPE_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name }),
      };

      try {
        const response = await getData(query, false, bodyData);
        if (response?.status === 200) {
          const data = response.data?.data?.data || [];
          // Add unique key if id is missing
          const dataWithKeys = data.map((item, index) => ({
            ...item,
            key: item.id || `costtype-${index}`,
          }));

          setTableData(dataWithKeys);
          setMetaData({
            current: response.data?.data?.current_page || 1,
            defaultCurrent: response?.data?.data?.from || 1,
            defaultPageSize: response?.data?.data?.per_page || 10,
            total: response.data?.data?.total || 0,
          });
        } else {
          setFetchError("Could not load this list.");
        }
      } catch (error) {
        console.error("Cost type fetch error:", error);
        setFetchError("Could not load this list.");
      } finally {
        setLoading(false);
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllCostTypes(filterData);
  }, [refresh, filterData, getAllCostTypes]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = useCallback(() => {
    costTypeForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  }, [costTypeForm]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedCostType = useCallback(
    async (id) => {
      setLoading(true);
      try {
        // Confirm before deleting
        Modal.confirm({
          title: "Confirm Deletion",
          content: "Are you sure you want to delete this Cost Type?",
          okText: "Yes, Delete",
          okType: "danger",
          cancelText: "Cancel",
          onOk: async () => {
            try {
              const response = await deleteData(
                `${COST_TYPE_DELETE_ENDPOINT}/${id}`,
              );

              if (
                response &&
                (response?.code === 200 || response?.status === 200)
              ) {
                refetch();
              }
            } catch (error) {
              console.error("Delete cost type error:", error);
            } finally {
              setLoading(false);
            }
          },
          onCancel: () => {
            setLoading(false);
          },
        });
      } catch (error) {
        console.error("Delete confirmation error:", error);
        setLoading(false);
      }
    },
    [refetch],
  );

  const handleCostTypeSubmit = useCallback(
    async (formValues) => {
      setLoading(true);
      const isEditMode = !!formValues.id;

      // Simplified request body based on API requirements
      const bodyData = {
        name: formValues?.name,
        // Including only the required field as specified in the API documentation
      };

      try {
        let response;

        if (isEditMode) {
          // Edit operation - PUT request
          response = await putData(
            `${COST_TYPE_EDIT_ENDPOINT}/${formValues.id}`,
            bodyData,
          );
        } else {
          // Create operation - POST request
          response = await postData(COST_TYPE_ADD_ENDPOINT, bodyData);
        }

        if (response && (response?.code === 200 || response?.status === 200)) {
          onCloseModal();
          refetch();
        }
      } catch (error) {
        console.error(
          `${isEditMode ? "Edit" : "Create"} cost type error:`,
          error,
        );
      } finally {
        setLoading(false);
      }
    },
    [onCloseModal, refetch],
  );

  const openModalForEdit = useCallback(
    (record) => {
      setIsEdit(true);
      costTypeForm.setFieldsValue({
        id: record.id,
        name: record.name,
      });
      setIsModalOpen(true);
    },
    [costTypeForm],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Cost Type Name",
        dataIndex: "name",
        width: "35%",
        key: "name",
        ellipsis: true,
      },
      {
        title: "Action",
        key: "action",
        width: "15%",
        render: (_, record) => (
          <div
            className="action-buttons"
            style={{ display: "flex", gap: "8px" }}
          >
            {hasPermission([NO_PERMISSION_REQUIRED]) && (
              <Button
                type="primary"
                size="small"
                onClick={() => openModalForEdit(record)}
                className="edit-button"
                style={{ backgroundColor: "#1890ff" }}
              >
                Edit
              </Button>
            )}
            {hasPermission([NO_PERMISSION_REQUIRED]) && (
              <Button
                type="primary"
                danger
                size="small"
                onClick={() => deleteSelectedCostType(record.id)}
                className="delete-button"
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [openModalForEdit, deleteSelectedCostType],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllCostTypes}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllCostTypes],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([NO_PERMISSION_REQUIRED])}
                addButtonText={
                  hasPermission([NO_PERMISSION_REQUIRED])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Cost Type Configuration</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllCostTypes(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                rowClassName={(record, index) =>
                  index % 2 === 0 ? "table-row-light" : "table-row-dark"
                }
                locale={{
                  emptyText: loading
                    ? "Loading data..."
                    : "No cost types found",
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isEdit ? (
              <span style={{ color: "#1890ff" }}>Edit Cost Type</span>
            ) : (
              <span style={{ color: "#52c41a" }}>Create New Cost Type</span>
            )}
          </div>
        }
        onCancel={onCloseModal}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
        destroyOnClose
        width={520}
        centered
        bodyStyle={{ padding: "24px" }}
      >
        <Form
          form={costTypeForm}
          layout="vertical"
          onFinish={handleCostTypeSubmit}
          initialValues={{}}
          requiredMark="optional"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                label="Cost Type Name"
                rules={[
                  {
                    required: true,
                    message: "Cost Type Name is required",
                  },
                  {
                    max: 100,
                    message: "Cost Type Name cannot exceed 100 characters",
                  },
                ]}
              >
                <Input
                  placeholder="Enter cost type name"
                  autoFocus
                  autoComplete="off"
                  style={{ borderRadius: "4px" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={16}
            className="form-actions"
            style={{ marginTop: "12px" }}
          >
            <Col span={12}>
              <Button
                type="default"
                danger
                style={{ width: "100%", borderRadius: "4px" }}
                onClick={onCloseModal}
              >
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%", borderRadius: "4px" }}
                loading={loading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
